import { createContext, useContext, useCallback, type ReactNode } from "react";
import { useQueryStates, parseAsString, parseAsArrayOf } from "nuqs";

interface FilterContextType {
  // Shared filters (used by multiple pages) - stored as arrays for multi-select support
  dateField: string;
  closers: string[];
  setters: string[];
  contents: string[];
  platforms: string[];
  coaches: string[];

  // Dashboard/Leads filters - now arrays for multi-select
  funnels: string[];
  countries: string[];
  sources: string[];
  leadTypes: string[];

  // Setters for array values
  setDateField: (value: string) => void;
  setClosers: (value: string[]) => void;
  setSetters: (value: string[]) => void;
  setContents: (value: string[]) => void;
  setPlatforms: (value: string[]) => void;
  setCoaches: (value: string[]) => void;
  setFunnels: (value: string[]) => void;
  setCountries: (value: string[]) => void;
  setSources: (value: string[]) => void;
  setLeadTypes: (value: string[]) => void;

  // Convenience getters for single-select UI (returns first item or empty string)
  closer: string;
  setter: string;
  content: string;
  funnel: string;
  country: string;
  source: string;
  leadType: string;

  // Convenience setters for single-select UI (wraps value in array)
  setCloser: (value: string) => void;
  setSetter: (value: string) => void;
  setContent: (value: string) => void;
  setFunnel: (value: string) => void;
  setCountry: (value: string) => void;
  setSource: (value: string) => void;
  setLeadType: (value: string) => void;

  // Utility functions
  clearAllFilters: () => void;
  hasActiveFilters: boolean;
}

const FilterContext = createContext<FilterContextType | null>(null);

export function FilterProvider({ children }: { children: ReactNode }) {
  // Use nuqs to sync filters with URL query params
  // All filters are now arrays to support multi-select UIs
  const [filters, setFilters] = useQueryStates(
    {
      // Core filter settings
      dateField: parseAsString.withDefault("timestamp"),

      // All filters as arrays using comma-separated format (?key=a,b,c)
      // This format is cleaner in URLs and easier to read
      closers: parseAsArrayOf(parseAsString).withDefault([]),
      setters: parseAsArrayOf(parseAsString).withDefault([]),
      contents: parseAsArrayOf(parseAsString).withDefault([]),
      platforms: parseAsArrayOf(parseAsString).withDefault([]),
      coaches: parseAsArrayOf(parseAsString).withDefault([]),
      funnels: parseAsArrayOf(parseAsString).withDefault([]),
      countries: parseAsArrayOf(parseAsString).withDefault([]),
      sources: parseAsArrayOf(parseAsString).withDefault([]),
      leadTypes: parseAsArrayOf(parseAsString).withDefault([]),
    },
    {
      history: "push",
    }
  );

  // Setters
  const setDateField = useCallback(
    (value: string) => setFilters({ dateField: value || "timestamp" }),
    [setFilters]
  );

  // Array setters (for multi-select)
  const setClosers = useCallback(
    (value: string[]) => setFilters({ closers: value }),
    [setFilters]
  );
  const setSetters = useCallback(
    (value: string[]) => setFilters({ setters: value }),
    [setFilters]
  );
  const setContents = useCallback(
    (value: string[]) => setFilters({ contents: value }),
    [setFilters]
  );
  const setPlatforms = useCallback(
    (value: string[]) => setFilters({ platforms: value }),
    [setFilters]
  );
  const setCoaches = useCallback(
    (value: string[]) => setFilters({ coaches: value }),
    [setFilters]
  );
  const setFunnels = useCallback(
    (value: string[]) => setFilters({ funnels: value }),
    [setFilters]
  );
  const setCountries = useCallback(
    (value: string[]) => setFilters({ countries: value }),
    [setFilters]
  );
  const setSources = useCallback(
    (value: string[]) => setFilters({ sources: value }),
    [setFilters]
  );
  const setLeadTypes = useCallback(
    (value: string[]) => setFilters({ leadTypes: value }),
    [setFilters]
  );

  // Single-value setters (convenience for single-select UIs)
  const setCloser = useCallback(
    (value: string) => setFilters({ closers: value ? [value] : [] }),
    [setFilters]
  );
  const setSetter = useCallback(
    (value: string) => setFilters({ setters: value ? [value] : [] }),
    [setFilters]
  );
  const setContent = useCallback(
    (value: string) => setFilters({ contents: value ? [value] : [] }),
    [setFilters]
  );
  const setFunnel = useCallback(
    (value: string) => setFilters({ funnels: value ? [value] : [] }),
    [setFilters]
  );
  const setCountry = useCallback(
    (value: string) => setFilters({ countries: value ? [value] : [] }),
    [setFilters]
  );
  const setSource = useCallback(
    (value: string) => setFilters({ sources: value ? [value] : [] }),
    [setFilters]
  );
  const setLeadType = useCallback(
    (value: string) => setFilters({ leadTypes: value ? [value] : [] }),
    [setFilters]
  );

  // Clear all filters (resets to defaults)
  const clearAllFilters = useCallback(() => {
    setFilters({
      dateField: "timestamp",
      closers: [],
      setters: [],
      contents: [],
      platforms: [],
      coaches: [],
      funnels: [],
      countries: [],
      sources: [],
      leadTypes: [],
    });
  }, [setFilters]);

  // Check if any filters are active
  const hasActiveFilters =
    filters.closers.length > 0 ||
    filters.setters.length > 0 ||
    filters.contents.length > 0 ||
    filters.platforms.length > 0 ||
    filters.coaches.length > 0 ||
    filters.funnels.length > 0 ||
    filters.countries.length > 0 ||
    filters.sources.length > 0 ||
    filters.leadTypes.length > 0;

  return (
    <FilterContext.Provider
      value={{
        // Core settings
        dateField: filters.dateField,

        // Array filters (for multi-select)
        closers: filters.closers,
        setters: filters.setters,
        contents: filters.contents,
        platforms: filters.platforms,
        coaches: filters.coaches,
        funnels: filters.funnels,
        countries: filters.countries,
        sources: filters.sources,
        leadTypes: filters.leadTypes,

        // Convenience single-value getters (first item or empty)
        closer: filters.closers[0] || "",
        setter: filters.setters[0] || "",
        content: filters.contents[0] || "",
        funnel: filters.funnels[0] || "",
        country: filters.countries[0] || "",
        source: filters.sources[0] || "",
        leadType: filters.leadTypes[0] || "",

        // Array setters
        setDateField,
        setClosers,
        setSetters,
        setContents,
        setPlatforms,
        setCoaches,
        setFunnels,
        setCountries,
        setSources,
        setLeadTypes,

        // Single-value setters (convenience)
        setCloser,
        setSetter,
        setContent,
        setFunnel,
        setCountry,
        setSource,
        setLeadType,

        // Utilities
        clearAllFilters,
        hasActiveFilters,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
}

export function useFilters() {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error("useFilters must be used within a FilterProvider");
  }
  return context;
}
